import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';


import PageHeader from '../components/shared/PageHeader';
import ErrorBoundary from '../../components/ErrorBoundary';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showSuccess, showError, showInfo, showServerError, showWarning } from '../../utils/toastHelper';

function SettingsManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [settings, setSettings] = useState({
    company: {
      id: null,
      name: '',
      description: '',
      logo: '',
      status: '',
      contactInfo: {
        email: '',
        phone: '',
        address: ''
      },
      shopifyShopUrl: '',
      shopifyAccessToken: '',
      hasShopifyAccessToken: false,
      shopifyAccessTokenMasked: '',
      iyzicoApiKey: '',
      iyzicoSecretKey: '',
      iyzicoMerchantId: '',
      hasIyzicoApiKey: false,
      hasIyzicoSecretKey: false,
      heroTitle: '',
      heroSubtitle: '',
      brandColor: '',
      customDomain: ''
    }
  });
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [testingShopify, setTestingShopify] = useState(false);
  const [testingIyzico, setTestingIyzico] = useState(false);

  useEffect(() => {
    loadSettings();

    // Responsive davranış için pencere boyutu değişikliklerini izle
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/admin/settings');
      if (response.success) {
        const company = response.settings.company || {};
        const shopifyConfig = company.shopifyConfig || {};
        const iyzicoConfig = company.iyzicoConfig || {};

        setSettings({
          company: {
            ...company,
            // Spec: shopifyConfig.shopUrl
            shopifyShopUrl: shopifyConfig.shopUrl || '',
            // Access token
            shopifyAccessToken: '',
            hasShopifyAccessToken: Boolean(shopifyConfig.hasAccessToken),
            // Iyzico config
            iyzicoApiKey: '',
            iyzicoSecretKey: '',
            iyzicoMerchantId: iyzicoConfig.merchantId || '',
            hasIyzicoApiKey: Boolean(iyzicoConfig.hasApiKey),
            hasIyzicoSecretKey: Boolean(iyzicoConfig.hasSecretKey),
            heroTitle: company.heroTitle || '',
            heroSubtitle: company.heroSubtitle || '',
            brandColor: company.brandColor || '',
            customDomain: company.customDomain || ''
          }
        });

        if (response.warning) {
          showWarning(response.warning);
        }
      }
      setError(null);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setError('Ayarlar yüklenirken bir hata oluştu');
      showError('Ayarlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (section, field, value, isContactInfo = false) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...(isContactInfo ? {
          contactInfo: {
            ...prev[section].contactInfo,
            [field]: value
          }
        } : {
          [field]: value
        })
      }
    }));
  };

  const testShopifyConnection = async () => {
    if (!settings.company.shopifyShopUrl || !settings.company.shopifyAccessToken) {
      showError('Lütfen Shopify Shop URL ve Access Token bilgilerini girin');
      return;
    }

    try {
      setTestingShopify(true);
      const response = await apiRequest('/admin/settings/test-shopify', {
        method: 'POST',
        data: {
          shopifyShopUrl: settings.company.shopifyShopUrl,
          shopifyAccessToken: settings.company.shopifyAccessToken
        }
      });

      if (response.success) {
        showSuccess(response.message || 'Shopify bağlantısı başarılı!');
      } else {
        showError(response.message || 'Shopify bağlantısı test edilemedi');
      }
    } catch (error) {
      console.error('Failed to test Shopify connection:', error);
      if (error.isAuthError) {
        return;
      }
      if (error.apiError) {
        showServerError();
      } else if (error.data?.message) {
        showError(error.data.message);
      } else {
        showError(error.message || 'Shopify bağlantısı test edilemedi');
      }
    } finally {
      setTestingShopify(false);
    }
  };

  const testIyzicoConnection = async () => {
    if (!settings.company.iyzicoApiKey || !settings.company.iyzicoSecretKey) {
      showError('Lütfen Iyzico API Key ve Secret Key bilgilerini girin');
      return;
    }

    try {
      setTestingIyzico(true);
      const response = await apiRequest('/admin/settings/test-iyzico', {
        method: 'POST',
        data: {
          iyzicoApiKey: settings.company.iyzicoApiKey,
          iyzicoSecretKey: settings.company.iyzicoSecretKey
        }
      });

      if (response.success) {
        const message = response.message || 'Iyzico bağlantısı başarılı!';
        if (response.testResult && response.testResult.totalCount !== undefined) {
          showSuccess(`${message} (${response.testResult.totalCount} ürün bulundu)`);
        } else {
          showSuccess(message);
        }
      } else {
        showError(response.message || 'Iyzico bağlantısı test edilemedi');
      }
    } catch (error) {
      console.error('Failed to test Iyzico connection:', error);
      if (error.isAuthError) {
        return;
      }
      if (error.apiError) {
        showServerError();
      } else if (error.data?.message) {
        showError(error.data.message);
      } else {
        showError(error.message || 'Iyzico bağlantısı test edilemedi');
      }
    } finally {
      setTestingIyzico(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        showError('Logo dosyası 5MB\'dan küçük olmalıdır');
        return;
      }
      setSelectedLogo(file);

      try {
        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('logo', file);

        const response = await apiRequest('/admin/settings/logo', {
          method: 'POST',
          data: formData
        });
        if (response.success) {
          setSettings(prev => ({
            ...prev,
            company: {
              ...prev.company,
              logo: response.logo
            }
          }));
          showSuccess('Logo başarıyla güncellendi');
        }
      } catch (error) {
        console.error('Failed to upload logo:', error);
        showError('Logo yüklenirken bir hata oluştu');
      } finally {
        setUploadingLogo(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      const dataToSend = {
        companyInfo: {
          name: settings.company.name,
          description: settings.company.description,
          contactInfo: settings.company.contactInfo
        },
        // OpenAPI: top-level shopifyShopUrl & shopifyAccessToken
        shopifyShopUrl: settings.company.shopifyShopUrl,
        heroTitle: settings.company.heroTitle,
        heroSubtitle: settings.company.heroSubtitle,
        brandColor: settings.company.brandColor || null,
        customDomain: settings.company.customDomain
      };

      // Access token yalnızca kullanıcı yeni bir değer girdiyse gönder
      const currentShopifyToken = settings.company.shopifyAccessToken?.trim() || '';
      if (currentShopifyToken !== '') {
        dataToSend.shopifyAccessToken = currentShopifyToken;
      }

      // Iyzico credentials - yalnızca kullanıcı yeni bir değer girdiyse gönder
      const currentIyzicoApiKey = settings.company.iyzicoApiKey?.trim() || '';
      if (currentIyzicoApiKey !== '') {
        dataToSend.iyzicoApiKey = currentIyzicoApiKey;
      }

      const currentIyzicoSecretKey = settings.company.iyzicoSecretKey?.trim() || '';
      if (currentIyzicoSecretKey !== '') {
        dataToSend.iyzicoSecretKey = currentIyzicoSecretKey;
      }

      // Iyzico merchantId - her zaman gönder (sensitif değil)
      const currentIyzicoMerchantId = settings.company.iyzicoMerchantId?.trim() || '';
      if (currentIyzicoMerchantId !== '') {
        dataToSend.iyzicoMerchantId = currentIyzicoMerchantId;
      }

      const response = await apiRequest('/admin/settings', {
        method: 'POST',
        data: dataToSend
      });

      if (response.success) {
        // Response'dan gelen yeni değerleri yükle (özellikle maskelenmiş hallerini)
        const company = response.settings.company || {};
        const shopifyConfig = company.shopifyConfig || {};
        const iyzicoConfig = company.iyzicoConfig || {};

        setSettings({
          company: {
            ...company,
            shopifyShopUrl: shopifyConfig.shopUrl || '',
            shopifyAccessToken: '',
            hasShopifyAccessToken: Boolean(shopifyConfig.hasAccessToken),
            // Iyzico config
            iyzicoApiKey: '',
            iyzicoSecretKey: '',
            iyzicoMerchantId: iyzicoConfig.merchantId || '',
            hasIyzicoApiKey: Boolean(iyzicoConfig.hasApiKey),
            hasIyzicoSecretKey: Boolean(iyzicoConfig.hasSecretKey),
            heroTitle: company.heroTitle || '',
            heroSubtitle: company.heroSubtitle || '',
            brandColor: company.brandColor || '',
            customDomain: company.customDomain || ''
          }
        });
        setIsEditing(false);
        showSuccess('Ayarlar başarıyla güncellendi');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      showError('Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid p-0" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Ayarlar yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid p-0" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container-fluid p-0" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div className="sticky-top bg-white pt-2 pb-2" style={{ zIndex: 1020, top: '70px', borderBottom: isEditing ? '1px solid #dee2e6' : 'none' }}>
          <PageHeader
          title="Şirket Ayarları"
          description="Şirket bilgilerinizi ve entegrasyon ayarlarınızı buradan yönetebilirsiniz."
          breadcrumbs={[{ label: 'Dashboard', path: '/admin' }, { label: 'Ayarlar' }]}
          actions={
            !isEditing ? (
              <button
                type="button"
                className="btn btn-primary btn-sm-mobile"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit me-2"></i>
                Düzenle
              </button>
            ) : (
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm-mobile mb-0"
                  onClick={() => {
                    setIsEditing(false);
                    loadSettings();
                  }}
                  disabled={saving}
                >
                  <i className="fas fa-times me-2"></i>
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm-mobile mb-0"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Kaydet
                    </>
                  )}
                </button>
              </div>
            )
          }
        />
        </div>

        <div className="card border-0 shadow-sm mt-3">
          <div className="card-body p-4">
            {isEditing && (
              <div className="alert alert-info mb-4">
                <i className="fas fa-info-circle me-2"></i>
                Düzenleme modu açık. Değişikliklerinizi yapıp <strong>Kaydet</strong> butonuna tıklayın.
              </div>
            )}

            <form>
              {/* Şirket Ayarları */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">Şirket Bilgileri</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex flex-column flex-md-row gap-4 align-items-center align-items-md-start">
                      {/* Logo Bölümü */}
                      <div className="logo-section">
                        <label className="form-label">Logo</label>
                        <div
                          className={`logoContainer border rounded p-4 bg-light ${isEditing ? 'cursor-pointer' : ''}`}
                          onClick={() => isEditing && !uploadingLogo && document.getElementById('logoInput').click()}
                          style={{
                            cursor: isEditing ? 'pointer' : 'default',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {settings.company.logo ? (
                            <>
                              <img
                                src={settings.company.logo}
                                alt="Şirket Logosu"
                                className="img-fluid mb-2"
                                style={{
                                  maxHeight: '100px',
                                  maxWidth: '100%',
                                  objectFit: 'contain',
                                  display: 'block',
                                  margin: '0 auto'
                                }}
                              />
                              {isEditing && (
                                <div className="text-center text-muted small mt-2">
                                  <small>PNG, JPG veya JPEG (max. 5MB)</small>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center text-muted">
                              <i className="fas fa-image fa-3x mb-3"></i>
                              <p className="mb-0">Logo yüklenmemiş</p>
                              {isEditing && (
                                <small className="d-block mt-2">PNG, JPG veya JPEG (max. 5MB)</small>
                              )}
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          id="logoInput"
                          className="d-none"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={handleLogoChange}
                          disabled={!isEditing || uploadingLogo}
                        />
                      </div>

                      {/* Şirket Bilgileri */}
                      <div className="flex-grow-1 w-100">
                        <div className="mb-3">
                          <label className="form-label">Şirket Adı</label>
                          <input
                            type="text"
                            className="form-control"
                            value={settings.company.name}
                            onChange={(e) => handleChange('company', 'name', e.target.value)}
                            disabled={!isEditing}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Açıklama</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={settings.company.description}
                            onChange={(e) => handleChange('company', 'description', e.target.value)}
                            disabled={!isEditing}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* İletişim Bilgileri */}
                  <div className="col-12 col-md-4">
                    <label className="form-label">E-posta</label>
                    <input
                      type="email"
                      className="form-control"
                      value={settings.company.contactInfo.email}
                      onChange={(e) => handleChange('company', 'email', e.target.value, true)}
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label">Telefon</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={settings.company.contactInfo.phone}
                      onChange={(e) => handleChange('company', 'phone', e.target.value, true)}
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label">Adres</label>
                    <textarea
                      className="form-control"
                      rows={isMobile ? "2" : "3"}
                      value={settings.company.contactInfo.address}
                      onChange={(e) => handleChange('company', 'address', e.target.value, true)}
                      disabled={!isEditing}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Site Görünümü */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">Site Görünümü</h6>
                <p className="text-muted small mb-3">
                  Müşterilerinizin giriş ve kayıt sayfalarında göreceği karşılama metinleri.
                </p>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Karşılama Başlığı</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ör: Hoş Geldiniz"
                      value={settings.company.heroTitle}
                      onChange={(e) => handleChange('company', 'heroTitle', e.target.value)}
                      disabled={!isEditing}
                    />
                    <small className="text-muted">Giriş sayfasında büyük başlık olarak görünür</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Alt Başlık / Slogan</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ör: Size özel ürünler, düzenli olarak kapınızda."
                      value={settings.company.heroSubtitle}
                      onChange={(e) => handleChange('company', 'heroSubtitle', e.target.value)}
                      disabled={!isEditing}
                    />
                    <small className="text-muted">Başlığın altında açıklayıcı metin</small>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">Giriş Sayfası Panel Rengi</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        style={{ width: '48px', height: '38px', padding: '2px' }}
                        value={settings.company.brandColor || '#1e293b'}
                        onChange={(e) => handleChange('company', 'brandColor', e.target.value)}
                        disabled={!isEditing}
                      />
                      {settings.company.brandColor && isEditing && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleChange('company', 'brandColor', '')}
                          title="Varsayılana dön"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                    <small className="text-muted">Sol panel arka plan rengi. Boş bırakılırsa koyu lacivert.</small>
                  </div>
                </div>
              </div>

              {/* Özel Alan Adı (Custom Domain) */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">
                  <i className="fas fa-globe me-2"></i>Özel Alan Adı
                </h6>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Alan Adı</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-link"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="abone.sirketiniz.com"
                        value={settings.company.customDomain || ''}
                        onChange={(e) => handleChange('company', 'customDomain', e.target.value)}
                        disabled={!isEditing}
                      />
                    </div>
                    {settings.company.customDomain ? (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-info-circle text-primary me-1"></i>
                        DNS yapılandırması için platform yöneticinizle iletişime geçin.
                      </small>
                    ) : (
                      <small className="text-muted d-block mt-1">
                        Müşterilerinizin erişeceği özel alan adı
                      </small>
                    )}
                  </div>
                </div>
              </div>

              {/* Shopify Entegrasyonu */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">Shopify Entegrasyonu</h6>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Shopify Shop URL</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa-brands fa-shopify"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="https://your-shop.myshopify.com"
                        value={settings.company.shopifyShopUrl || ''}
                        onChange={(e) => handleChange('company', 'shopifyShopUrl', e.target.value)}
                        disabled={!isEditing}
                      />
                    </div>
                    <small className="text-muted">Shopify mağaza URL'inizi girin (örn: https://your-shop.myshopify.com)</small>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Shopify Access Token</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-key"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={settings.company.hasShopifyAccessToken ? '••••••••••••••••' : 'Access token girin'}
                        value={settings.company.shopifyAccessToken || ''}
                        onChange={(e) => handleChange('company', 'shopifyAccessToken', e.target.value)}
                        disabled={!isEditing}
                      />
                    </div>
                    {settings.company.hasShopifyAccessToken ? (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-check-circle text-success me-1"></i>
                        Access Token yapılandırılmış
                      </small>
                    ) : (
                      <small className="text-muted d-block mt-1">Shopify Admin API Access Token</small>
                    )}
                  </div>
                  {isEditing && (
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={testShopifyConnection}
                        disabled={testingShopify || !settings.company.shopifyShopUrl || !settings.company.shopifyAccessToken}
                      >
                        {testingShopify ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Test Ediliyor...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-plug me-2"></i>
                            Bağlantıyı Test Et
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Iyzico Entegrasyonu */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">Iyzico Entegrasyonu</h6>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label">Üye İşyeri Numarası (Merchant ID)</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-store"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Merchant ID girin"
                        value={settings.company.iyzicoMerchantId || ''}
                        onChange={(e) => handleChange('company', 'iyzicoMerchantId', e.target.value)}
                        disabled={!isEditing}
                      />
                    </div>
                    <small className="text-muted">iyzico panelindeki Üye İşyeri Numaranız</small>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label">Iyzico API Key</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-key"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={settings.company.hasIyzicoApiKey ? '••••••••••••••••' : 'API Key girin'}
                        value={settings.company.iyzicoApiKey || ''}
                        onChange={(e) => handleChange('company', 'iyzicoApiKey', e.target.value)}
                        disabled={!isEditing}
                      />
                    </div>
                    {settings.company.hasIyzicoApiKey && (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-check-circle text-success me-1"></i>
                        API Key yapılandırılmış
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label">Iyzico Secret Key</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-lock"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={settings.company.hasIyzicoSecretKey ? '••••••••••••••••' : 'Secret Key girin'}
                        value={settings.company.iyzicoSecretKey || ''}
                        onChange={(e) => handleChange('company', 'iyzicoSecretKey', e.target.value)}
                        disabled={!isEditing}
                      />
                    </div>
                    {settings.company.hasIyzicoSecretKey && (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-check-circle text-success me-1"></i>
                        Secret Key yapılandırılmış
                      </small>
                    )}
                  </div>
                  {isEditing && (
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={testIyzicoConnection}
                        disabled={testingIyzico || !settings.company.iyzicoApiKey || !settings.company.iyzicoSecretKey}
                      >
                        {testingIyzico ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Test Ediliyor...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-plug me-2"></i>
                            Bağlantıyı Test Et
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom duplicate save actions removed to rely on sticky top header */}
            </form>
          </div>
        </div>

        <style>
          {`
            .logo-upload-container {
              transition: all 0.3s ease;
            }

            .logo-upload-container:hover {
              border-color: var(--bs-orange) !important;
            }

            .logo-preview {
              cursor: pointer;
            }

            .logo-preview:hover .logo-overlay {
              opacity: 1 !important;
            }

            .logo-overlay {
              transition: opacity 0.2s ease;
            }

            .custom-file-upload {
              position: relative;
            }

            .custom-file-upload input[type="file"] {
              cursor: pointer;
            }

            .logoContainer {
              position: relative;
              display: block;
              cursor: pointer;
              min-height: 160px;
              transition: all 0.2s ease;
              width: 100%;
            }

            .logoImage {
              max-width: 100%;
              max-height: 100px;
              object-fit: contain;
              display: block;
              margin: 0 auto;
            }

            .overlay {
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: rgba(0, 0, 0, 0.5);
              opacity: 0;
              transition: opacity 0.2s ease;
            }

            .logoContainer:hover {
              background-color: #f0f0f0;
            }

            .logoContainer:hover .overlay {
              opacity: 1;
              cursor: pointer;
            }

            .text-olive {
              color: var(--kito-olive);
            }
            
            .btn-orange {
              background-color: var(--kito-orange);
              border-color: var(--kito-orange);
              color: white;
            }
            
            .btn-orange:hover {
              background-color: #d05c2e;
              border-color: #d05c2e;
            }
            
            .btn-orange:focus {
              box-shadow: 0 0 0 0.25rem rgba(232, 108, 58, 0.25);
            }
            
            .btn-orange:active {
              background-color: #c55427;
              border-color: #c55427;
            }
            
            /* Mobil ve tablet için ek stiller */
            @media (max-width: 767px) {
              .logo-section {
                width: 100%;
                margin-bottom: 1rem;
              }
              
              .logoContainer {
                min-height: 140px;
                padding: 1rem !important;
              }
            }
          `}
        </style>
      </div>
    </ErrorBoundary>
  );
}

export default SettingsManagement;